import React, { useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import CampaignTimer from './CampaignTimer';
import { EmailWithSelection, CampaignTimerConfig } from '../types';
import { getPoliteGreeting } from '../utils/emailUtils';
import { recordSentEmail } from '../lib/emailService';

interface EmailCampaignResultsProps {
  timerConfig: CampaignTimerConfig;
  setTimerConfig: React.Dispatch<React.SetStateAction<CampaignTimerConfig>>;
  emailsWithSelection: EmailWithSelection[];
  onPauseResume: () => void;
  campaignInProgress: boolean;
  setCampaignInProgress: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function EmailCampaignResults({
  timerConfig,
  setTimerConfig,
  emailsWithSelection,
  onPauseResume,
  campaignInProgress,
  setCampaignInProgress
}: EmailCampaignResultsProps) {
  
  const sendNextEmail = useCallback(async () => {
    if (timerConfig.isPaused) return;
    
    const sentCount = timerConfig.totalSent;
    
    if (sentCount < emailsWithSelection.length) {
      // Get the next recipient
      const nextEmailData = emailsWithSelection[sentCount];
      
      // Create personalized greeting based on the recipient's email
      const greeting = getPoliteGreeting(nextEmailData.email);
      
      // Get the email template and replace placeholder with personalized greeting
      let emailTemplate = timerConfig.emailTemplate;
      
      // Parse email template parts
      const templateLines = emailTemplate.split('\n');
      const subject = templateLines.find(line => line.startsWith('Subject:'))?.substring(8).trim() || 'New Campaign Update';
      
      // Personalize the content
      emailTemplate = emailTemplate.replace(/Hi there,|Hello,|Dear {{recipient}},/, greeting);
      const emailContent = emailTemplate.replace(/\{\{recipient\}\}/g, nextEmailData.email);
      
      try {
        // Record the sent email in the database
        await recordSentEmail(nextEmailData.email, subject, `Campaign from ${new Date().toLocaleDateString()}`);

        console.log(`Email recorded for ${nextEmailData.email}`);
        toast.success(`Email recorded for ${nextEmailData.email}`);

        // Update the timer configuration
        setTimerConfig(prev => ({
          ...prev,
          totalSent: prev.totalSent + 1,
          nextSendTime: new Date(Date.now() + prev.interval * 1000)
        }));
      } catch (error) {
        const err = error as Error;
        console.error(`Error recording email for ${nextEmailData.email}:`, err);
        toast.error(`Failed to record email: ${err.message}`);

        // Pause the campaign on error
        setCampaignInProgress(false);
        setTimerConfig(prev => ({ ...prev, isPaused: true }));
      }
    } else {
      // Campaign completed
      setCampaignInProgress(false);
      toast.success('Campaign completed successfully!');
      
      setTimerConfig(prev => ({
        ...prev,
        nextSendTime: null
      }));
    }
  }, [timerConfig, emailsWithSelection, setTimerConfig, setCampaignInProgress]);

  // Effect to handle sending emails at intervals
  useEffect(() => {
    if (!campaignInProgress || timerConfig.isPaused) return;
    
    let timeout: NodeJS.Timeout | undefined;
    
    if (timerConfig.nextSendTime) {
      const now = new Date();
      const timeUntilNext = Math.max(0, timerConfig.nextSendTime.getTime() - now.getTime());
      
      timeout = setTimeout(() => {
        sendNextEmail();
      }, timeUntilNext);
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [campaignInProgress, timerConfig.nextSendTime, timerConfig.isPaused, sendNextEmail]);

  return (
    <CampaignTimer
      config={timerConfig}
      onConfigChange={setTimerConfig}
      onPauseResume={onPauseResume}
    />
  );
}